/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable react-hooks/exhaustive-deps */
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  FormControlLabel,
  IconButton,
  SvgIcon,
  Typography,
} from '@mui/material';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  Button,
  ConfirmationMessageModal,
  FullScreenLoader,
  Loader,
  TextFieldWrapperWithInputMask,
  ToggleSwitch,
} from '@Components/index';
import MainSectionLayout from '@Components/MainSectionLayout';
import Radio from '@Components/Radio/Radio.component';
import { StyledRadioGroup } from '@Components/ReportConfig/ReportConfig.styles';
import commonConstants from '@Constants/common.constants';
import Icons from '@Constants/Icons.constants';
import { RotationNumbersProps } from '@Containers/UserCallerIds/AbstractRotationCallerIds/AbstractSystemNumbersModal/AbstractSystemNumbersModal.types';
import VerifiedOwnNumberModal from '@Containers/UserCallerIds/AbstractRotationCallerIds/VerifiedOwnNumberModal';
import ForwardingNumber from '@Containers/UserCallerIds/ForwardingNumber';
import SmsCodeVerificationModal from '@Containers/UserCallerIds/SmsCodeVerification';
import { AddNewCallerIdSchema } from '@Containers/UserCallerIds/UserCallerIdsSchema';
import {
  AddNewNumberInputs,
  CallerIdRotationData,
  DeleteRotationCallerIdRequest,
  VerificationStatus,
  VoiceProvider,
} from '@Models/callerIds.models';
import { ScriptsQuery } from '@Models/scripts.models';
import {
  useStirShakenMutation,
  useSupportStirShakenQuery,
  useChangeEnablingRotationCallerByIdMutation,
  useDeleteRotationCallerIdMutation,
  useGetRotationCallerIdListQuery,
  useGetTprSettingQuery,
  useVerifyRotationCallerMutation,
  useVerifyRotationCallerStatusQuery,
} from '@Services/userCallerIds.service';
import {
  BaseAlertProps,
  RotationCallerIdSource,
  RotationPhoneTypes,
} from '@Typings/global.types';
import {
  formatNumber,
  getDateMMDDYYYYHHMM,
  getRTKQuery,
  unformatUSNumber,
} from '@Utils/common/common.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import {
  useTokenTprValueHook,
  useLiveConnectSuite,
} from '@Utils/hooks/useTokenValues.hook';

import AbstractRotationSystemNumbersModal from '../AbstractSystemNumbersModal';

import { StyledForm, StyledStack } from './AbstractRotationNumbers.styles';

const AbstractRotationNumbersContainer: FunctionComponent<RotationNumbersProps> =
  (props: RotationNumbersProps): JSX.Element => {
    const { isAdmin, provider } = props;

    const { t } = useTranslation();
    const { triggerAlert } = useAlert();

    const [showAddNumberToRotationForm, setShowAddNumberToRotationForm] =
      useState(false);
    const [disableVerifyRotationButton, setDisableVerifyRotationButton] =
      useState(false);
    const [rotationCallerIdToVerifyStatus, setRotationCallerIdToVerifyStatus] =
      useState<string>('');
    const [alertState, setAlertState] = useState<BaseAlertProps>({
      openAlert: false,
    });
    const [
      rotationDeleteConfirmationMessage,
      setRotationDeleteConfirmationMessage,
    ] = useState<React.ReactNode | null>(null);
    const [
      isRotationDeleteConfirmationModalOpen,
      setIsRotationDeleteConfirmationModalOpen,
    ] = useState(false);
    const [isRotationVerifiedModalOpen, setIsRotationVerifiedModalOpen] =
      useState(false);
    const [
      rotationDeleteConfirmationModalData,
      setRotationDeleteConfirmationModalData,
    ] = useState<DeleteRotationCallerIdRequest>({
      id: '',
      source: RotationCallerIdSource.OWN,
      userId: '',
    });
    const [rotationVerificationStatus, setRotationVerificationStatus] =
      useState<{
        status: VerificationStatus | null;
      }>({ status: null });
    const [pinCode, setPinCode] = useState('');
    const [showTwilioRotationModal, setShowTwilioRotationModal] =
      useState<boolean>(false);
    const [numberType, setNumberType] = useState<RotationPhoneTypes>(
      RotationPhoneTypes.SYSTEM
    );
    const [isSmsCodeVerificationModalOpen, setIsSmsCodeVerificationModalOpen] =
      useState(false);
    const [isCallerIdInvalidated, setIsCallerIdInvalidated] = useState(false);

    const location = useLocation();
    const rotationQuery: ScriptsQuery = useMemo(
      () => getRTKQuery(location.search),
      [location.search]
    );

    const hasTpr = useTokenTprValueHook();
    const hasLiveConnectSuite = useLiveConnectSuite();
    const isTprDisplayed =
      (isAdmin && rotationQuery.hasUserTpr === 'true') || (!isAdmin && hasTpr);
    const isStirShakenDisplayed =
      (isAdmin && rotationQuery.hasUserLiveConnect === 'true') ||
      (!isAdmin && hasLiveConnectSuite);

    // load a rotation caller ids list
    const {
      data: dataOfRotationCallerIdList = [],
      isLoading: loadingRotationCallerIdList,
      isFetching: fetchingRotationCallerIdList,
      isError: isErrorForGetRotationCallerIdList,
      error: errorForGetRotationCallerIdList,
      refetch: refetchRotationCallerIdList,
    } = useGetRotationCallerIdListQuery({
      userId: rotationQuery.userId?.toString() || '',
      provider: provider.toString(),
    });

    // change enabling of STIR/SHAKEN
    const [updateStirShaken, updateStirShakenMethod] = useStirShakenMutation();

    const { data: userSupportStirShaken } = useSupportStirShakenQuery(
      rotationQuery.userId?.toString() || ''
    );

    // verify own rotation callerId
    const [verifyRotationCaller, verifyRotationCallerMethods] =
      useVerifyRotationCallerMutation();

    // change enabling of rotation callerId
    const [
      changeEnablingRotationCallerById,
      changeEnablingRotationCallerByIdMethods,
    ] = useChangeEnablingRotationCallerByIdMutation();

    // delete rotation callerId
    const [deleteRotationCallerId, deleteRotationCallerIdMethods] =
      useDeleteRotationCallerIdMutation();

    const {
      data: dataForVerifyRotationCallerStatus,
      isLoading: loadingForVerifyRotationCallerStatus,
      isSuccess: successVerifyRotationCallerStatus,
      isFetching: fetchingVerifyRotationCallerStatus,
      error: errorOnVerifyRotationCallerStatus,
    } = useVerifyRotationCallerStatusQuery(rotationCallerIdToVerifyStatus, {
      skip: !rotationCallerIdToVerifyStatus,
      pollingInterval: commonConstants.POLLING_INTERVAL,
    });

    const { data: dataOfTprSettings } = useGetTprSettingQuery({
      id: rotationQuery.userId?.toString() || '',
      provider: provider.toString(),
    });

    const availableLimitToBuy = dataOfTprSettings
      ? dataOfTprSettings.availableTprAmount - dataOfTprSettings.usedTprAmount
      : 0;

    /**
     * Deletes rotation callerId
     * @param id Id of the caller
     * @param phoneNumber phone number of the rotation callerId
     * @param source source of the callerId
     */
    const handleDeleteRotationCallerId = (
      id: string,
      phoneNumber: string,
      source: RotationCallerIdSource
    ) => {
      const userId: string = rotationQuery.userId
        ? rotationQuery.userId?.toString()
        : '';
      setRotationDeleteConfirmationModalData({
        id,
        source,
        userId,
      });
      // Formatting phone to US / UA formats
      const phone = formatNumber(phoneNumber);

      const message: React.ReactNode = (
        <Typography
          variant="main"
          sx={{
            '& .phone': {
              color: 'secondary.main',
            },
          }}
        >
          <Trans
            i18nKey="common.confirmationMessages.confirmDelete"
            values={{ phone }}
            components={{
              strong: <strong />,
              span: <span className="phone" />,
            }}
          />
        </Typography>
      );
      setRotationDeleteConfirmationMessage(message);
      setIsRotationDeleteConfirmationModalOpen(true);
    };

    /** Handles toggle enabling state change */
    const handleEnablingRotationCallerIdChange = (id: string | undefined) => {
      if (id) {
        return changeEnablingRotationCallerById({
          id,
          userId: rotationQuery.userId?.toString() || '',
        });
      }
    };

    /** Handles toggle enabling state change */
    const handleAddNumberToStirShaken = (
      callerId: string | undefined,
      stirShaken: boolean
    ) => {
      if (callerId) {
        updateStirShaken({
          callerId,
          stirShaken,
          userId: rotationQuery.userId?.toString() || '',
        });
      }
    };

    /**
     * Hides the confirmation modal and deletes the rotation callerId
     * @param data DeleteRotationCallerIdRequest object
     * @returns delete mutation
     */
    const deleteCallerIdActionHandler = (
      data: DeleteRotationCallerIdRequest
    ) => {
      setRotationDeleteConfirmationModalData({
        id: '',
        source: RotationCallerIdSource.OWN,
        userId: '',
      });
      setIsRotationDeleteConfirmationModalOpen(false);
      if (data.id && data.source) {
        return deleteRotationCallerId(data);
      }
    };

    const addNumberToRotation = () => {
      setShowAddNumberToRotationForm(numberType === RotationPhoneTypes.OWN);
      setShowTwilioRotationModal(numberType === RotationPhoneTypes.SYSTEM);
    };

    /**
     * Add New Rotation Number Form Methods: Handles form validation using useForm hook
     */
    const rotationMethods = useForm<AddNewNumberInputs>({
      resolver: yupResolver(AddNewCallerIdSchema()),
      mode: 'onChange',
      shouldUnregister: true,
    });

    /**
     * Error Alerts for the Get Rotation Caller Ids Query
     */
    useEffect(() => {
      if (
        isErrorForGetRotationCallerIdList &&
        errorForGetRotationCallerIdList
      ) {
        setDisableVerifyRotationButton(false);
        triggerAlert(errorForGetRotationCallerIdList, setAlertState);
      }
    }, [
      triggerAlert,
      errorForGetRotationCallerIdList,
      isErrorForGetRotationCallerIdList,
    ]);

    const resetRotationCallback = useCallback(() => {
      setPinCode('');
      setRotationCallerIdToVerifyStatus('');
      setRotationVerificationStatus({
        status: null,
      });
      setShowAddNumberToRotationForm(false);
    }, []);

    /**
     * Add New Caller Id rotation Form Methods: Updates the form state on submit
     */
    const onRotationSubmitHandler: SubmitHandler<AddNewNumberInputs> = (
      formData
    ) => {
      if (formData) {
        setPinCode('');
        setRotationVerificationStatus({
          status: null,
        });

        const formattedPhone = formData.phone;
        const filteredDigits = formattedPhone.replace(/\D/g, '');
        let planePhone: string;
        if (filteredDigits.startsWith('0')) {
          planePhone = `+38${unformatUSNumber(formattedPhone)}`;
        } else {
          planePhone = `+1${unformatUSNumber(formattedPhone)}`;
        }

        if (
          filteredDigits.length === commonConstants.PHONE_NUMBER_DIGITS_COUNT
        ) {
          setDisableVerifyRotationButton(true);
          return verifyRotationCaller({
            phone: planePhone,
            source: RotationCallerIdSource.OWN,
            provider: provider.toString(),
          });
        }

        rotationMethods.setError('phone', {
          type: 'manual',
          message: t('userCallerIds.Invalid input'),
        });
      }
    };

    useEffect(() => {
      if (
        rotationVerificationStatus.status === VerificationStatus.INITIATED &&
        provider === VoiceProvider.TELNYX
      ) {
        setIsSmsCodeVerificationModalOpen(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rotationVerificationStatus]);

    // refetch rotation callerIds list on successfully added a verified number
    useEffect(() => {
      if (isCallerIdInvalidated) {
        resetRotationCallback();
        setIsCallerIdInvalidated(false);

        refetchRotationCallerIdList().catch(console.error);
      }
    }, [
      isCallerIdInvalidated,
      refetchRotationCallerIdList,
      resetRotationCallback,
    ]);

    const resolveVerifyMessageKey = (): string => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const map: Record<VoiceProvider, string> = {
        [VoiceProvider.TWILIO]: 'userCallerIds.Twilio Verify Message',
        [VoiceProvider.TELNYX]: 'userCallerIds.Telnyx Verify Message',
      };

      const key = map[provider];
      if (!key) {
        throw new Error(`Unsupportable voice provider: ${provider}`);
      }

      return key;
    };

    const resolveVerifyInProgressMessageKey = (): string => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const map: Record<VoiceProvider, string> = {
        [VoiceProvider.TWILIO]: 'userCallerIds.Twilio Verify In Progress',
        [VoiceProvider.TELNYX]: 'userCallerIds.Telnyx Verify In Progress',
      };

      const key = map[provider];
      if (!key) {
        throw new Error(`Unsupportable voice provider: ${provider}`);
      }

      return key;
    };

    /** Rotation Caller Ids Table Loading State */
    const loadingRotation =
      loadingRotationCallerIdList || fetchingRotationCallerIdList;

    /**
     * Error and Success alerts for Verify Rotation CallerId
     */
    useEffect(() => {
      if (verifyRotationCallerMethods.error) {
        setDisableVerifyRotationButton(false);
        triggerAlert(verifyRotationCallerMethods.error, setAlertState);
      }
      if (verifyRotationCallerMethods.isSuccess) {
        setPinCode(verifyRotationCallerMethods.data.pin);
        setRotationVerificationStatus({ status: VerificationStatus.INITIATED });
        setRotationCallerIdToVerifyStatus(
          verifyRotationCallerMethods.data.identifier
        );
      }
    }, [
      verifyRotationCallerMethods.isSuccess,
      verifyRotationCallerMethods.error,
      verifyRotationCallerMethods.data,
      triggerAlert,
      rotationMethods,
      t,
    ]);

    /**
     * Error and Success alerts for switch STIR/SHAKEN
     */
    useEffect(() => {
      if (updateStirShakenMethod.error) {
        triggerAlert(updateStirShakenMethod.error, setAlertState);
      }
    }, [
      triggerAlert,
      updateStirShakenMethod.isError,
      updateStirShakenMethod.isSuccess,
      updateStirShakenMethod.error,
      updateStirShakenMethod.data,
    ]);

    /**
     * Error and Success alerts for Delete Own Rotation CallerId
     */
    useEffect(() => {
      if (
        deleteRotationCallerIdMethods.isError &&
        deleteRotationCallerIdMethods.error
      ) {
        triggerAlert(deleteRotationCallerIdMethods.error, setAlertState);
      }
      if (deleteRotationCallerIdMethods.isSuccess) {
        triggerAlert(
          commonConstants.GLOBAL_MESSAGES.ROTATION_NUMBER_DELETE,
          setAlertState
        );
      }
    }, [
      deleteRotationCallerIdMethods.isError,
      deleteRotationCallerIdMethods.isSuccess,
      deleteRotationCallerIdMethods.error,
      deleteRotationCallerIdMethods.data,
      triggerAlert,
    ]);

    /**
     * Error and Success alerts for changing rotation CallerId enabling
     */
    useEffect(() => {
      if (
        changeEnablingRotationCallerByIdMethods.isError &&
        changeEnablingRotationCallerByIdMethods.error
      ) {
        triggerAlert(
          changeEnablingRotationCallerByIdMethods.error,
          setAlertState
        );
      }
    }, [
      changeEnablingRotationCallerByIdMethods.isError,
      changeEnablingRotationCallerByIdMethods.isSuccess,
      changeEnablingRotationCallerByIdMethods.error,
      changeEnablingRotationCallerByIdMethods.data,
      triggerAlert,
    ]);

    /**
     * Verifying Rotation CallerId Status
     * This uses a polling API endpoint
     */
    useEffect(() => {
      if (errorOnVerifyRotationCallerStatus) {
        setDisableVerifyRotationButton(false);
        setRotationVerificationStatus({
          status: VerificationStatus.FAILED,
        });
        setRotationCallerIdToVerifyStatus('');
      }
      if (successVerifyRotationCallerStatus) {
        if (
          dataForVerifyRotationCallerStatus?.status ===
            VerificationStatus.VERIFIED &&
          !(
            loadingForVerifyRotationCallerStatus ||
            fetchingVerifyRotationCallerStatus
          )
        ) {
          setDisableVerifyRotationButton(false);
          setRotationVerificationStatus({
            status: VerificationStatus.VERIFIED,
          });
          resetRotationCallback();
          setIsRotationVerifiedModalOpen(true);
          refetchRotationCallerIdList();
        } else if (
          dataForVerifyRotationCallerStatus?.status ===
            VerificationStatus.FAILED &&
          !(
            loadingForVerifyRotationCallerStatus ||
            fetchingVerifyRotationCallerStatus
          )
        ) {
          setDisableVerifyRotationButton(false);
          setRotationVerificationStatus({
            status: VerificationStatus.FAILED,
          });
          setRotationCallerIdToVerifyStatus('');
        }
      }
    }, [
      resetRotationCallback,
      triggerAlert,
      refetchRotationCallerIdList,
      dataForVerifyRotationCallerStatus,
      loadingForVerifyRotationCallerStatus,
      successVerifyRotationCallerStatus,
      fetchingVerifyRotationCallerStatus,
      errorOnVerifyRotationCallerStatus,
    ]);

    const cancelAddNumberToRotation = () => {
      resetRotationCallback();
    };

    const openReverifyForm = (phone: string) => {
      rotationMethods.setValue(
        'phone',
        phone.startsWith('+380') ? phone.slice(3) : phone.slice(2)
      );
      setShowAddNumberToRotationForm(true);
    };

    const resolveRotationCallerIdSource = () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const map: Record<VoiceProvider, RotationCallerIdSource> = {
        [VoiceProvider.TWILIO]: RotationCallerIdSource.TWILIO,
        [VoiceProvider.TELNYX]: RotationCallerIdSource.TELNYX,
      };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const value: RotationCallerIdSource = map[provider];
      if (!value) {
        throw new Error(`Unsupportable voice provider: ${provider}`);
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return value;
    };

    /**
     * Constructs the UI (row) of the rotation caller-ids table
     * @param id unique id of the caller
     * @param phone phone number of the caller
     * @param date createdAt date
     * @param status verification status
     * @param isEnabled phone number enabled
     * @param source source of caller id
     * @param handleDeletingRotationCallerId callback method to delete the rotation caller-id
     * @param handleEnablingRotationCallerId callback method to change the rotation caller-id enabling
     * @returns a single row, populating the data
     */
    const CallerIdRotationTableRow = (
      id: string,
      phone: string,
      stirShaken: boolean,
      date: string,
      isEnabled: boolean,
      status: string,
      source: string,
      supportStirShaken: boolean,
      isRevereficationRequired: boolean,
      handleDeletingRotationCallerId: (
        id: string,
        phone: string,
        source: RotationCallerIdSource
      ) => void,
      handleEnablingRotationCallerId: (id: string) => void
    ): JSX.Element => {
      let verificationSection;

      if (
        status === VerificationStatus.VERIFIED &&
        source === RotationCallerIdSource.OWN
      ) {
        verificationSection = (
          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{ minWidth: '101px' }}
          >
            <IconButton
              onClick={() =>
                handleDeletingRotationCallerId(
                  id,
                  phone,
                  RotationCallerIdSource.OWN
                )
              }
              className="delete-icon"
              aria-label="delete"
            >
              <SvgIcon>{Icons.delete}</SvgIcon>
            </IconButton>
          </Box>
        );
      } else if (
        status === VerificationStatus.FAILED &&
        source === RotationCallerIdSource.OWN
      ) {
        verificationSection = (
          <Button
            className="reverify-button"
            disabled={loadingRotation || disableVerifyRotationButton}
            onClick={() => openReverifyForm(phone)}
            sx={{ minWidth: '90px' }}
          >
            {t('userCallerIds.REVERIFY')}
          </Button>
        );
      } else if (isAdmin) {
        verificationSection = (
          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{ minWidth: '101px' }}
          >
            <IconButton
              onClick={() =>
                handleDeletingRotationCallerId(
                  id,
                  phone,
                  resolveRotationCallerIdSource()
                )
              }
              className="delete-icon"
              aria-label="delete"
            >
              <SvgIcon>{Icons.delete}</SvgIcon>
            </IconButton>
          </Box>
        );
      } else {
        verificationSection = <Box sx={{ minWidth: '101px' }} />;
      }

      return (
        <StyledStack key={id}>
          <Box className="phone-and-warning">
            <Typography
              className="phone-number"
              variant="main"
              sx={{ color: isEnabled ? 'text.primary' : 'text.disabled' }}
            >
              {/* eslint-disable-next-line react/destructuring-assignment */}
              {formatNumber(phone)}
            </Typography>

            {isRevereficationRequired && source === RotationCallerIdSource.OWN && (
              <Typography className="warning-text" variant="main">
                This Caller Id should be reverified
              </Typography>
            )}
          </Box>

          {isStirShakenDisplayed && provider === VoiceProvider.TWILIO ? (
            <Box className="stir-shaken">
              <FormControlLabel
                title={
                  source !== resolveRotationCallerIdSource() ||
                  !stirShaken ||
                  !supportStirShaken
                    ? t('common.messages.STIR-SHAKEN-NOT-SUPPORT')
                    : ''
                }
                control={
                  <ToggleSwitch
                    checked={stirShaken}
                    disabled={
                      source !== resolveRotationCallerIdSource() ||
                      stirShaken ||
                      !supportStirShaken
                    }
                    onChange={() =>
                      handleAddNumberToStirShaken(phone, !stirShaken)
                    }
                    color="secondary"
                  />
                }
                label={
                  <>
                    <Typography variant="main">
                      {t('common.messages.STIR-SHAKEN')}
                    </Typography>
                  </>
                }
              />
            </Box>
          ) : (
            ''
          )}

          <Typography
            className="date"
            variant="main"
            sx={{ color: isEnabled ? 'text.primary' : 'text.disabled' }}
          >
            {t('userCallerIds.Created Date', {
              interpolation: {
                escapeValue: false,
              },
              date: getDateMMDDYYYYHHMM(new Date(date), true),
            })}
          </Typography>
          <Typography
            className="number-type"
            variant="main"
            sx={{ color: isEnabled ? 'text.primary' : 'text.disabled' }}
          >
            {source === RotationCallerIdSource.OWN
              ? t('userCallerIds.Own Number')
              : t('userCallerIds.System Number')}
          </Typography>
          {status !== VerificationStatus.FAILED ? (
            <Box className="enable-number">
              <FormControlLabel
                control={
                  <ToggleSwitch
                    checked={isEnabled}
                    onChange={() => handleEnablingRotationCallerId(id)}
                    color="secondary"
                  />
                }
                label={
                  <Typography variant="main">
                    {t(
                      isEnabled
                        ? 'common.messages.Enabled'
                        : 'common.messages.Disabled'
                    )}
                  </Typography>
                }
              />
            </Box>
          ) : (
            <Box sx={{ minWidth: '130px' }} />
          )}
          {verificationSection}
        </StyledStack>
      );
    };

    /** Handles Phone Type Radio Button Group Change */
    const handleNumberTypeChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setNumberType(
        (event.target as HTMLInputElement).value as RotationPhoneTypes
      );
    };

    const ChooseNumberType = (
      <Box display="flex" alignItems="center">
        <StyledRadioGroup
          row
          aria-label="phone-type"
          name="phone-type-radio-group"
          value={numberType}
          onChange={handleNumberTypeChange}
          sx={{ ml: 0 }}
        >
          <FormControlLabel
            value={RotationPhoneTypes.SYSTEM}
            control={<Radio />}
            label={t('userCallerIds.System')}
          />
          {!isAdmin && (
            <FormControlLabel
              value={RotationPhoneTypes.OWN}
              control={<Radio />}
              label={t('userCallerIds.Own')}
            />
          )}
        </StyledRadioGroup>
        {!showAddNumberToRotationForm && (
          <Button
            onClick={() => addNumberToRotation()}
            color="secondary"
            variant="outlined"
            disabled={
              numberType === RotationPhoneTypes.SYSTEM &&
              availableLimitToBuy < 1
            }
            sx={{ minWidth: 12.6, ml: 1.6 }}
          >
            {t('common.messages.Add Number')}
          </Button>
        )}
      </Box>
    );

    /**
     * Conditionally shows/hides the Add Rotation Number Form
     */
    const AddNumberToRotationForm = (
      <>
        <Typography
          variant="main"
          fontSize="18px"
          fontWeight="bold"
          textAlign="left"
          sx={{ my: 3.2 }}
        >
          {t('userCallerIds.Trusted Phone Head')}
        </Typography>
        {!isAdmin && <ForwardingNumber />}
        {!isAdmin && (
          <Typography
            variant="main"
            fontSize="16px"
            fontWeight="bold"
            textAlign="left"
            paddingLeft={0}
            sx={{ my: 3.2 }}
          >
            {t('userCallerIds.Trusted Phone Subhead')}
          </Typography>
        )}
        {!showAddNumberToRotationForm && ChooseNumberType}
        {!showAddNumberToRotationForm && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ width: '100%', backgroundColor: '#F2F5F9', p: 1, my: 1.2 }}
          >
            <SvgIcon>{Icons.infoOutlined}</SvgIcon>
            <Typography variant="main">
              {numberType === RotationPhoneTypes.OWN ? (
                t('userCallerIds.Unlimited own numbers')
              ) : (
                <Trans
                  i18nKey="userCallerIds.Available numbers amount"
                  components={{ strong: <strong /> }}
                  values={{ amount: availableLimitToBuy }}
                />
              )}
            </Typography>
          </Box>
        )}

        {/* Clicking on the "Add Number" button will hide the button and show the form. */}
        {showAddNumberToRotationForm && (
          <>
            <Typography className="phone-number" mb={1.6} variant="main">
              {t(resolveVerifyMessageKey())}
            </Typography>
            <FormProvider {...rotationMethods}>
              <StyledForm
                component="form"
                onSubmit={rotationMethods.handleSubmit(onRotationSubmitHandler)}
              >
                <Box display="flex">
                  <TextFieldWrapperWithInputMask
                    name="phone"
                    placeholder={`${t('userCallerIds.Enter your number')} *`}
                    className="add-number-input"
                    mask={commonConstants.PHONE_NUMBER_INPUT_MASK}
                  />

                  {/* Verification in progress */}
                  {VerificationStatus.INITIATED ===
                    rotationVerificationStatus.status &&
                    provider === VoiceProvider.TWILIO && (
                      <Box display="flex" alignItems="center" ml={1.6}>
                        <Loader size="2.4rem" sx={{ mr: 1.6 }} />
                        <Typography
                          className="verification-initiated"
                          variant="main"
                        >
                          <Trans
                            i18nKey={resolveVerifyInProgressMessageKey()}
                            values={{ pinCode }}
                            components={{ span: <span className="pincode" /> }}
                          />
                        </Typography>
                      </Box>
                    )}

                  {/* Verification failed */}
                  {VerificationStatus.FAILED ===
                    rotationVerificationStatus.status && (
                    <Box display="flex" alignItems="center" ml={1.6}>
                      <Typography
                        className="verification-initiated-failed"
                        variant="main"
                      >
                        {t('userCallerIds.Verification Failed')}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box>
                  <Button
                    variant="text"
                    type="button"
                    color="secondary"
                    sx={{ mr: 2.4 }}
                    disabled={loadingRotation || disableVerifyRotationButton}
                    onClick={() => cancelAddNumberToRotation()}
                  >
                    {t('common.confirmationModal.CANCEL')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={loadingRotation || disableVerifyRotationButton}
                  >
                    {VerificationStatus.FAILED ===
                    rotationVerificationStatus.status
                      ? t('userCallerIds.REVERIFY')
                      : t('userCallerIds.VERIFY')}
                  </Button>
                </Box>
              </StyledForm>
            </FormProvider>
          </>
        )}
      </>
    );

    /**
     * Rotation Caller ids table component
     */
    const CallerIdRotationTableComponent = (
      <>
        <Box sx={{ overflowX: 'auto', minWidth: '100%' }}>
          {dataOfRotationCallerIdList.length > 0 &&
            dataOfRotationCallerIdList
              .filter(
                (item) =>
                  (isAdmin && item.status !== VerificationStatus.FAILED) ||
                  !isAdmin
              )
              .map(
                (item: CallerIdRotationData): JSX.Element =>
                  CallerIdRotationTableRow(
                    item.id,
                    item.callerId,
                    item.stirShaken,
                    item.createdAt,
                    item.isEnabled,
                    item.status,
                    item.source,
                    item.supportStirShaken,
                    item.isRevereficationRequired,
                    handleDeleteRotationCallerId,
                    handleEnablingRotationCallerIdChange
                  )
              )}
        </Box>
        <AbstractRotationSystemNumbersModal
          userId={rotationQuery.userId}
          isOpen={showTwilioRotationModal}
          setIsOpen={setShowTwilioRotationModal}
          availableLimitToBuy={availableLimitToBuy}
          supportStirShaken={!!userSupportStirShaken}
          provider={provider}
        />
        {/* Verified Confirmation Modal */}
        <VerifiedOwnNumberModal
          isOpen={isRotationVerifiedModalOpen}
          setIsOpen={setIsRotationVerifiedModalOpen}
        />
        {/* Delete Confirmation Modal */}
        <ConfirmationMessageModal
          isModalOpen={isRotationDeleteConfirmationModalOpen}
          setIsModalOpen={setIsRotationDeleteConfirmationModalOpen}
          message={rotationDeleteConfirmationMessage}
          onClick={() =>
            deleteCallerIdActionHandler(rotationDeleteConfirmationModalData)
          }
        />
        <SmsCodeVerificationModal
          phone={rotationMethods.watch('phone') || ''}
          isOpen={isSmsCodeVerificationModalOpen}
          setIsOpen={setIsSmsCodeVerificationModalOpen}
          setDisableVerifyButton={setDisableVerifyRotationButton}
          setIsCallerIdInvalidated={setIsCallerIdInvalidated}
          isTpr
        />
        {dataOfRotationCallerIdList.length === 0 && (
          <Typography
            variant="main"
            fontWeight="bold"
            textAlign="center"
            sx={{ my: 3.2, color: 'action.disabled' }}
          >
            {t('common.table.No data to show')}
          </Typography>
        )}
      </>
    );

    return (
      <>
        {isTprDisplayed ? (
          <>
            {loadingRotation ? (
              <FullScreenLoader data-testid="loader" />
            ) : (
              <MainSectionLayout
                alertState={alertState}
                filters={AddNumberToRotationForm}
                table={CallerIdRotationTableComponent}
              />
            )}
          </>
        ) : (
          <Typography
            variant="main"
            fontWeight="bold"
            textAlign="center"
            sx={{ my: 3.2, color: 'action.disabled' }}
          >
            {t('common.messages.No TPR')}
          </Typography>
        )}
      </>
    );
  };

export default AbstractRotationNumbersContainer;
